/**
 * Quell-Metadaten am Ticket (Spec 5.2): Validierung gegen
 * `sources.metadata_schema` und Anzeige-Steuerung ueber `sources.metadata_display`.
 *
 * Zwei Regeln aus der Spec, die hier zusammenkommen:
 *
 *   1. „validiert gegen `sources.metadata_schema`" — eine Quelle sagt, welche
 *      Schluessel sie liefert und von welchem Typ sie sind.
 *   2. „**Unbekannte Keys werden trotzdem gespeichert** und generisch
 *      angezeigt" — das Schema beschreibt, es sperrt nicht ab. Ein neues Feld
 *      in der App darf nie dazu fuehren, dass ein Ticket nicht ankommt.
 *
 * Deshalb ist die Validierung bewusst **nicht** streng: sie prueft nur, was
 * das Schema explizit fordert, und laesst alles Uebrige durch.
 *
 * **Unterstuetzte JSON-Schema-Teilmenge** (ADR 0006): `type: 'object'`,
 * `properties` mit `type` (`string` | `number` | `integer` | `boolean` |
 * `array` | `object`), `enum`, `required`. Alles andere im Schema wird
 * ignoriert, nicht abgelehnt.
 *
 * Doku: docs/services/ingest.md
 */
import { z } from 'zod';
// ─────────────────────────────────────────────── metadata_schema (Eingang)
export const METADATA_PROPERTY_TYPES = [
    'string',
    'number',
    'integer',
    'boolean',
    'array',
    'object',
];
export const metadataPropertyTypeSchema = z.enum(METADATA_PROPERTY_TYPES);
/** Eine Eigenschaft im `metadata_schema` einer Quelle. */
export const metadataPropertySchema = z.object({
    type: metadataPropertyTypeSchema.optional(),
    /** Erlaubte Werte. Nur fuer Skalare sinnvoll. */
    enum: z.array(z.union([z.string(), z.number(), z.boolean()])).min(1).optional(),
    /** Reine Dokumentation — wird nicht geprueft. */
    description: z.string().max(500).optional(),
});
/**
 * Die unterstuetzte JSON-Schema-Teilmenge. `type: 'object'` ist der einzige
 * sinnvolle Wurzeltyp — Metadaten sind immer ein Objekt.
 */
export const metadataSchemaSchema = z.object({
    type: z.literal('object').optional(),
    properties: z.record(z.string().min(1).max(100), metadataPropertySchema).optional(),
    required: z.array(z.string().min(1).max(100)).max(50).optional(),
});
/** Freie Metadaten, wie sie am Ticket landen. */
export const metadataSchema = z.record(z.string(), z.unknown());
function typeOf(value) {
    if (value === null)
        return 'null';
    if (Array.isArray(value))
        return 'array';
    const type = typeof value;
    if (type === 'string')
        return 'string';
    if (type === 'boolean')
        return 'boolean';
    if (type === 'number')
        return Number.isInteger(value) ? 'integer' : 'number';
    if (type === 'object')
        return 'object';
    return 'unknown';
}
function matchesType(value, expected) {
    const actual = typeOf(value);
    // `null` gilt als „nicht gesetzt" und wird nie als Typfehler gewertet: eine
    // App, die ein Feld kennt, aber gerade keinen Wert hat, schickt `null`.
    if (actual === 'null')
        return true;
    if (expected === 'number')
        return actual === 'number' || actual === 'integer';
    return actual === expected;
}
/**
 * Prueft Metadaten gegen das Schema einer Quelle.
 *
 * Gibt **alle** Verstoesse zurueck (nicht nur den ersten) — der Aufrufer baut
 * daraus eine Fehlermeldung. Kein Schema hinterlegt ⇒ keine Verstoesse.
 * Unbekannte Schluessel sind nie ein Verstoss (Spec 5.2).
 */
export function validateMetadata(metadata, schema) {
    if (!schema)
        return [];
    const issues = [];
    for (const key of schema.required ?? []) {
        const value = metadata[key];
        if (value === undefined || value === null) {
            issues.push({ key, problem: 'missing', expected: 'ein Wert' });
        }
    }
    for (const [key, property] of Object.entries(schema.properties ?? {})) {
        const value = metadata[key];
        if (value === undefined)
            continue;
        if (property.type !== undefined && !matchesType(value, property.type)) {
            issues.push({ key, problem: 'type', expected: property.type });
            continue;
        }
        if (property.enum !== undefined && value !== null && !property.enum.includes(value)) {
            issues.push({ key, problem: 'enum', expected: property.enum.join(', ') });
        }
    }
    return issues;
}
/** Kurze, deutsche Zusammenfassung der Verstoesse — fuer `ValidationError`. */
export function describeMetadataIssues(issues) {
    return issues
        .slice(0, 5)
        .map((issue) => issue.problem === 'missing'
        ? `„${issue.key}" fehlt`
        : issue.problem === 'type'
            ? `„${issue.key}" muss vom Typ ${issue.expected} sein`
            : `„${issue.key}" muss einer von: ${issue.expected} sein`)
        .join('; ');
}
// ─────────────────────────────────────────── metadata_display (Anzeige)
/**
 * Wie ein Metadaten-Wert im Ticket dargestellt wird (Spec 5.2:
 * „Key-Label-Mapping, Codeblock fuer console_log usw.").
 */
export const METADATA_FORMATS = ['text', 'code', 'list', 'link', 'json', 'boolean'];
export const metadataFormatSchema = z.enum(METADATA_FORMATS);
export const metadataFieldDisplaySchema = z.object({
    /** Beschriftung im UI. Fehlt sie, wird der Schluessel angezeigt. */
    label: z.string().min(1).max(120).optional(),
    format: metadataFormatSchema.optional(),
    /** `false` blendet den Schluessel aus (z. B. interne Korrelations-IDs). */
    visible: z.boolean().optional(),
});
export const metadataDisplaySchema = z.object({
    /** Reihenfolge der bekannten Schluessel; alles Uebrige folgt alphabetisch. */
    order: z.array(z.string().min(1).max(100)).max(50).optional(),
    fields: z.record(z.string().min(1).max(100), metadataFieldDisplaySchema).optional(),
});
/** Ohne Angabe: Arrays als Liste, Objekte als JSON, Booleans als Ja/Nein. */
function defaultFormat(value) {
    if (Array.isArray(value))
        return 'list';
    if (typeof value === 'boolean')
        return 'boolean';
    if (value !== null && typeof value === 'object')
        return 'json';
    if (typeof value === 'string' && /^https?:\/\//i.test(value))
        return 'link';
    return 'text';
}
/**
 * Bringt die rohen Metadaten in Anzeigereihenfolge und haengt Label und
 * Format an. **Unbekannte Schluessel bleiben drin** (Spec 5.2) — sie
 * erscheinen hinter den konfigurierten, alphabetisch sortiert.
 */
export function buildMetadataEntries(metadata, display) {
    const fields = display?.fields ?? {};
    const configuredOrder = display?.order ?? [];
    const known = configuredOrder.filter((key) => key in metadata);
    const rest = Object.keys(metadata)
        .filter((key) => !known.includes(key))
        .sort((a, b) => a.localeCompare(b));
    return [...known, ...rest]
        .filter((key) => fields[key]?.visible !== false)
        .map((key) => {
        const value = metadata[key];
        const field = fields[key];
        return {
            key,
            label: field?.label ?? key,
            format: field?.format ?? defaultFormat(value),
            value,
        };
    });
}
//# sourceMappingURL=metadata.js.map